import { config } from "./config";
import { getAccessToken } from "./auth";
import type { DemoRoleId } from "./roles";

export class ApiError extends Error {
  status: number;
  waking: boolean;
  constructor(message: string, status: number, waking = false) {
    super(message);
    this.status = status;
    this.waking = waking;
  }
}

async function sleep(ms: number) {
  await new Promise((r) => setTimeout(r, ms));
}

type FetchOpts = {
  method?: string;
  body?: unknown;
  roleId?: DemoRoleId | string;
  retries?: number;
};

export async function apiFetch<T>(path: string, opts: FetchOpts = {}): Promise<T> {
  const base = config.apiUrl.replace(/\/$/, "");
  if (!base) {
    // Offline / pre-deploy: use local seed fallback via static import handled by callers
    throw new ApiError("Demo API URL is not configured", 503, false);
  }

  const retries = opts.retries ?? 8;
  let lastErr: unknown;

  for (let attempt = 0; attempt <= retries; attempt++) {
    const token = await getAccessToken();
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };
    if (token) headers.Authorization = `Bearer ${token}`;
    if (opts.roleId) headers["X-Demo-Role"] = opts.roleId;

    try {
      const res = await fetch(`${base}${path}`, {
        method: opts.method ?? "GET",
        headers,
        body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
      });

      if (res.status === 503 || res.status === 504) {
        const body = await res.json().catch(() => ({}));
        const waking = Boolean(body.waking) || res.status === 503;
        if (attempt < retries && waking) {
          await sleep(Math.min(2000 * 2 ** attempt, 8000));
          continue;
        }
        throw new ApiError(body.error || "Demo database is starting", res.status, true);
      }

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new ApiError(body.error || `Request failed (${res.status})`, res.status);
      }

      if (res.status === 204) return undefined as T;
      return (await res.json()) as T;
    } catch (err) {
      lastErr = err;
      if (err instanceof ApiError && !err.waking) throw err;
      if (attempt < retries) {
        await sleep(Math.min(1500 * 2 ** attempt, 8000));
        continue;
      }
    }
  }

  throw lastErr instanceof Error ? lastErr : new ApiError("Request failed", 500, true);
}

export async function checkHealth(): Promise<{ ok: boolean }> {
  const base = config.apiUrl.replace(/\/$/, "");
  if (!base) return { ok: true };
  const res = await fetch(`${base}/health`);
  return { ok: res.ok };
}

export async function checkReady(roleId?: DemoRoleId): Promise<{ ready: boolean; waking?: boolean }> {
  return apiFetch("/ready", { roleId, retries: 10 });
}
